import { CONSENT_PATH } from './paths';

/*
 * A signed-out owner who opens the consent link signs in first and then lands on the consent screen again. Only the request id is kept
 * (in this tab's sessionStorage, for as long as the request lives), never an address: the way back is rebuilt here, so nothing a
 * visitor puts in a link can become a redirect target after sign-in.
 */
const KEY = 'scopie.consent_return';
const LIFE_MS = 10 * 60_000;
/** The id's shape: what the server issues. Anything else never reaches an API path or a stored value. */
const REQUEST_ID = /^[A-Za-z0-9_-]{8,128}$/;

export const isRequestId = (v: unknown): v is string => typeof v === 'string' && REQUEST_ID.test(v);

export function stashConsentReturn(id: string, now = Date.now()): void {
  if (!isRequestId(id)) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ id, at: now }));
  } catch {
    // storage blocked: the owner opens the link from the app again after signing in
  }
}

function read(now: number): string | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as { id?: unknown; at?: unknown } | null;
    if (v && isRequestId(v.id) && typeof v.at === 'number' && now - v.at >= 0 && now - v.at < LIFE_MS) return v.id;
  } catch {
    // unreadable: treated as nothing stored
  }
  return null;
}

/** Whether a consent screen is waiting for this sign-in (the sign-in page says so). Does not remove it. */
export const hasConsentReturn = (now = Date.now()): boolean => read(now) !== null;

/** The consent address to open after sign-in, once; null when nothing waits. */
export function takeConsentReturn(now = Date.now()): string | null {
  const id = read(now);
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // nothing to do
  }
  return id ? `${CONSENT_PATH}?req=${id}` : null;
}

/** Forgets a waiting consent return (a sign-out: the next person to sign in on this tab must not land on someone else's request). */
export function clearConsentReturn(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // nothing to do
  }
}
